import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  BookOpen,
  Camera,
  ChevronLeft,
  Check,
  Search,
  X,
  Barcode
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported barcode formats for library books
const SUPPORTED_BARCODE_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,      // ISBN barcodes (13-digit)
  Html5QrcodeSupportedFormats.EAN_8,       // Short EAN codes
  Html5QrcodeSupportedFormats.UPC_A,       // 12-digit UPC (US)
  Html5QrcodeSupportedFormats.UPC_E,       // Compressed UPC
  Html5QrcodeSupportedFormats.CODE_39,     // Library internal barcodes
  Html5QrcodeSupportedFormats.CODE_128,    // Library internal barcodes
  Html5QrcodeSupportedFormats.CODABAR,     // Library barcodes (Codabar/NW-7)
  Html5QrcodeSupportedFormats.ITF,         // Interleaved 2 of 5
];

export const MobileBooks = ({ user }) => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    call_number: "",
    barcode: ""
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  // Start scanner when dialog opens - wait for DOM element to be ready
  useEffect(() => {
    let timeoutId;
    if (scannerOpen && !html5QrCodeRef.current) {
      // Small delay to ensure DOM element is rendered
      timeoutId = setTimeout(() => {
        const element = document.getElementById("mobile-barcode-reader");
        if (element) {
          startScanner();
        }
      }, 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      stopScanner();
    };
  }, [scannerOpen]);

  // Helper function to clean Codabar start/stop characters
  const cleanBarcodeValue = (barcode) => {
    if (!barcode) return barcode;
    // Codabar uses A, B, C, D as start/stop characters - remove them
    return barcode.replace(/^[ABCDabcd]/, '').replace(/[ABCDabcd]$/, '');
  };

  const startScanner = async () => {
    try {
      setScanning(true);
      html5QrCodeRef.current = new Html5Qrcode("mobile-barcode-reader", {
        formatsToSupport: SUPPORTED_BARCODE_FORMATS,
        verbose: false
      });
      
      // Get container width for responsive qrbox
      const containerWidth = document.getElementById("mobile-barcode-reader")?.offsetWidth || 300;
      const qrboxWidth = Math.min(containerWidth - 40, 280);
      const qrboxHeight = Math.min(120, qrboxWidth * 0.4);
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 15,  // Increased FPS for faster detection
          qrbox: { width: qrboxWidth, height: qrboxHeight },
          aspectRatio: 1.0,  // Square aspect ratio works better on mobile
          disableFlip: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true  // Use native BarcodeDetector API if available
          }
        },
        (decodedText, decodedResult) => {
          // Clean Codabar start/stop characters if present
          const cleanedBarcode = cleanBarcodeValue(decodedText);
          console.log("Scanned:", decodedText, "Cleaned:", cleanedBarcode, "Format:", decodedResult?.result?.format?.formatName);
          setFormData(prev => ({ ...prev, barcode: cleanedBarcode }));
          toast.success(`Scanned: ${cleanedBarcode}`);
          stopScanner();
          setScannerOpen(false);
        },
        (errorMessage) => {
          // Ignore scan errors (normal during scanning)
        }
      );
    } catch (err) {
      console.error("Failed to start scanner:", err);
      toast.error("Could not access camera");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScannerClose = () => {
    stopScanner();
    setScannerOpen(false);
  };

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data.slice(0, 20)); // Show recent 20
    } catch (error) {
      console.error("Failed to fetch books:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/books`, {
        title: formData.title,
        author: formData.author || "",
        call_number: formData.call_number || "",
        barcode: formData.barcode || "",
        branch: "",
        member: "",
        purpose_tags: [],
        notes: ""
      });

      toast.success("Book added!");
      setDialogOpen(false);
      resetForm();
      fetchBooks();
    } catch (error) {
      toast.error("Failed to add book");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      author: "",
      call_number: "",
      barcode: ""
    });
  };

  const filteredBooks = books.filter(book =>
    book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.barcode?.includes(searchQuery)
  );

  return (
    <div className="px-4 py-4" data-testid="mobile-books">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/mobile")}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold">Books</h1>
            <p className="text-xs text-muted-foreground">Library collection</p>
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          size="sm"
          className="bg-gradient-to-r from-emerald-500 to-teal-600 shadow-md"
          data-testid="mobile-add-book-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search books..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="mobile-book-search"
        />
      </div>

      {/* Books List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredBooks.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground mb-3" strokeWidth={1} />
            <p className="font-medium">No books found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery ? "Try a different search" : "Tap 'Add' to add a book"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredBooks.map((book) => (
            <Card key={book.id} className="overflow-hidden" data-testid={`mobile-book-${book.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-14 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-1">{book.title}</h3>
                    {book.author && (
                      <p className="text-xs text-muted-foreground mt-0.5">{book.author}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      {book.barcode && (
                        <Badge variant="outline" className="text-xs">
                          <Barcode className="h-3 w-3 mr-1" />
                          {book.barcode}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Book Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] rounded-2xl p-0 gap-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-heading">Add Book</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Barcode Scanner Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-14 border-dashed"
              onClick={() => setScannerOpen(true)}
              data-testid="mobile-scan-barcode-btn"
            >
              <Camera className="h-5 w-5 mr-2" />
              Scan Barcode
            </Button>

            {formData.barcode && (
              <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                <Barcode className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">{formData.barcode}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-6 w-6"
                  onClick={() => setFormData({ ...formData, barcode: "" })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-sm">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Book title"
                className="mt-1.5"
                data-testid="mobile-book-title"
              />
            </div>

            {/* Author */}
            <div>
              <Label htmlFor="author" className="text-sm">Author</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Author name"
                className="mt-1.5"
                data-testid="mobile-book-author"
              />
            </div>

            {/* Call Number */}
            <div>
              <Label htmlFor="call_number" className="text-sm">Call Number</Label>
              <Input
                id="call_number"
                value={formData.call_number}
                onChange={(e) => setFormData({ ...formData, call_number: e.target.value })}
                placeholder="e.g., 823.912 AUS"
                className="mt-1.5"
                data-testid="mobile-book-callnumber"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
                disabled={submitting}
                data-testid="mobile-submit-book-btn"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" /> Save
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={handleScannerClose}>
        <DialogContent className="max-w-[95vw] rounded-2xl p-0 gap-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-heading">Scan Barcode</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <div 
              id="mobile-barcode-reader" 
              ref={scannerRef}
              className="w-full rounded-lg overflow-hidden bg-black"
              style={{ minHeight: "300px" }}
            />
            <p className="text-center text-sm text-muted-foreground mt-3">
              {scanning ? "Center barcode in the box" : "Starting camera..."}
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleScannerClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  const manual = prompt("Enter barcode manually:");
                  if (manual) {
                    const cleaned = cleanBarcodeValue(manual.trim());
                    setFormData(prev => ({ ...prev, barcode: cleaned }));
                    toast.success("Barcode entered");
                    handleScannerClose();
                  }
                }}
              >
                Enter Manually
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
